// Which of a patient's numbers to text. notes-c has two phone columns:
// `tel_num` (the cell) and `home_phone` (added 2026-10-17). The rule, decided
// and final:
//   1. Candidates are the cell, then the home phone. Blanks are dropped; the
//      same number in two spellings counts once.
//   2. If EITHER number is opted out (sms_number_health "opt_out" / Do Not
//      Text), the patient gets no text at all.
//   3. Otherwise text the first candidate that is not "bad" (permanent Twilio
//      error). The cell wins when both are fine; a bad cell with a fine (or
//      untexted) home phone means the home phone is texted, automatically.
//   4. Both bad: no text — the email fallback / "known bad" warnings apply.
// The default is derived from sms_number_health per number; nothing is stored.
//
// The backend applies the same rule (backend/twilio_service/sms_phone.py);
// payloads built here carry `alt_phone` so it can retry on the other number.

import { smsE164 } from "./badNumbers";
import { loadNumberStatus } from "./numberStatus";

const CELL_FIELDS = ["tel_num", "phone", "phone_number", "phoneNumber", "Phone Number", "cell_phone"];
const HOME_FIELDS = ["home_phone", "homePhone", "Home Phone"];

const firstFilled = (row, fields) => {
  for (const field of fields) {
    const value = row?.[field];
    if (value == null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return "";
};

// 10-digit phone, the way every SMS badge in the app matches numbers
// (ResultsTable, AdminPage, PatientMessagingModal).
export function normalizePhone(phone) {
  if (!phone) return null;
  const digits = String(phone).replace(/\D/g, "");
  if (!digits) return null;
  if (digits.length === 11 && digits.startsWith("1")) return digits.slice(1);
  return digits.slice(-10);
}

/** The patient's cell as written on the row (any of the cell field names). */
export const cellPhoneOf = (row) => firstFilled(row, CELL_FIELDS);

/** The patient's home phone as written on the row. */
export const homePhoneOf = (row) => firstFilled(row, HOME_FIELDS);

/**
 * Rule 1: [{ slot: "cell" | "home", phone }] — cell first, blanks dropped,
 * one entry when both columns hold the same number.
 */
export const smsCandidates = (row) => {
  const out = [];
  const seen = new Set();
  [
    ["cell", cellPhoneOf(row)],
    ["home", homePhoneOf(row)],
  ].forEach(([slot, phone]) => {
    if (!phone) return;
    const key = smsE164(phone) || phone.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ slot, phone });
  });
  return out;
};

/** Every number on the row, as written (cell first). */
export const rowPhones = (row) => smsCandidates(row).map((c) => c.phone);

/** Set of 10-digit forms of every number on the row — for matching SMS from/to. */
export const rowPhonesNormalized = (row) => {
  const out = new Set();
  rowPhones(row).forEach((phone) => {
    const n = normalizePhone(phone);
    if (n) out.add(n);
  });
  return out;
};

/** Whether an SMS row's from/to belongs to this patient (any of their numbers). */
export const smsMatchesRow = (sms, row) => {
  const mine = rowPhonesNormalized(row);
  if (!mine.size) return false;
  return mine.has(normalizePhone(sms?.from)) || mine.has(normalizePhone(sms?.to));
};

const noPhone = (reason, candidates, optOut = false) => ({
  phone: null,
  slot: null,
  altPhone: null,
  altSlot: null,
  blocked: true,
  reason,
  optOut,
  candidates,
});

/**
 * Rules 2–4 over the row's candidates.
 *
 * @param statusOf (phone) => numberStatus status string, or null/undefined
 *   when it cannot tell — an unknown number is usable.
 * @returns {{
 *   phone: string|null,      number to text (null when blocked)
 *   slot: "cell"|"home"|null,
 *   altPhone: string|null,   the other usable candidate, for the backend's retry
 *   altSlot: "cell"|"home"|null,
 *   blocked: boolean,
 *   reason: null|"no_phone"|"opt_out"|"all_bad",
 *   optOut: boolean,
 *   candidates: [{ slot, phone, status }]
 * }}
 */
export const pickSmsPhone = (row, statusOf) => {
  const candidates = smsCandidates(row).map((c) => ({
    ...c,
    status: (typeof statusOf === "function" ? statusOf(c.phone) : null) || null,
  }));
  if (!candidates.length) return noPhone("no_phone", candidates);
  if (candidates.some((c) => c.status === "opt_out")) return noPhone("opt_out", candidates, true);
  const usable = candidates.filter((c) => c.status !== "bad");
  if (!usable.length) return noPhone("all_bad", candidates);
  const [pick, alt] = usable;
  return {
    phone: pick.phone,
    slot: pick.slot,
    altPhone: alt ? alt.phone : null,
    altSlot: alt ? alt.slot : null,
    blocked: false,
    reason: null,
    optOut: false,
    candidates,
  };
};

/**
 * pickSmsPhone over live sms_number_health statuses (utils/numberStatus,
 * batched + cached). Never throws: a lookup that fails leaves the number's
 * status null, i.e. usable, the same fail-open the bad-number guard has.
 */
export const resolveSmsPhone = async (row) => {
  const candidates = smsCandidates(row);
  const statuses = new Map();
  await Promise.all(
    candidates.map(async ({ phone }) => {
      try {
        const res = await loadNumberStatus(phone);
        statuses.set(phone, res?.status || null);
      } catch (_) {
        statuses.set(phone, null);
      }
    }),
  );
  return pickSmsPhone(row, (phone) => statuses.get(phone) || null);
};

/**
 * The `phone` / `alt_phone` pair for a backend send payload (send_form df1,
 * reminders): the primary candidate and, when there is one, the other.
 * Statuses are not consulted — the backend applies the good/bad rule itself
 * and retries on alt_phone. `digitsOnly` strips formatting the way the
 * existing payload builders do.
 */
export const payloadPhones = (row, { digitsOnly = false } = {}) => {
  const [first, second] = rowPhones(row);
  const fmt = (p) => (p ? (digitsOnly ? String(p).replace(/\D/g, "") : p) : "");
  return { phone: fmt(first), alt_phone: fmt(second) };
};

/** The other candidate than `phone` on the row, or "" — for `alt_phone`. */
export const altPhoneFor = (row, phone, { digitsOnly = false } = {}) => {
  const want = smsE164(phone);
  const other = rowPhones(row).find((p) => smsE164(p) !== want) || "";
  return digitsOnly ? other.replace(/\D/g, "") : other;
};

export const SLOT_LABEL = { cell: "Cell", home: "Home" };
